"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, Code as Code2, Activity, Bug, Sparkles, ArrowRight, ShieldCheck, Cpu } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png-wvc-sizes",
  alt: ""
};
const beats = [{
  step: "01",
  enTitle: "Build",
  viTitle: "Lắp Ráp",
  enDesc: "Hardware assembly, structural gear meshing, and sensor mounting on lab chassis.",
  viDesc: "Lắp ráp phần cứng cơ khí, kết cấu truyền động và tích hợp cảm biến thông minh.",
  iconName: "wrench",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_build.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_build.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_build.png-wvc-sizes",
  alt: "Young student assembling mechanical robotics parts with gears and sensors"
}, {
  step: "02",
  enTitle: "Code",
  viTitle: "Lập Trình",
  enDesc: "Visual block algorithms or line-based Python commands to steer actuators and logic.",
  viDesc: "Xây dựng thuật toán kéo thả hoặc viết mã Python điều khiển hệ thống chuyển động.",
  iconName: "code",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_code.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_code.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_code.png-wvc-sizes",
  alt: "Hands typing robotics control scripts and block algorithms on a lab tablet"
}, {
  step: "03",
  enTitle: "Test",
  viTitle: "Thử Nghiệm",
  enDesc: "Live bench calibration, ultrasonic distance checks, and real-time test runs.",
  viDesc: "Chạy thực địa trên thảm thử nghiệm, hiệu chuẩn cảm biến siêu âm và hành trình.",
  iconName: "test",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_test.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_test.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_test.png-wvc-sizes",
  alt: "Students benchmarking robotic vehicle on testing track with sensors active"
}, {
  step: "04",
  enTitle: "Solve",
  viTitle: "Giải Quyết",
  enDesc: "Isolating unexpected bugs, tracking telemetry, and fine-tuning engineering parameters.",
  viDesc: "Phân tích nguyên nhân lỗi, hiệu chỉnh thông số và tìm giải pháp kỹ thuật tối ưu.",
  iconName: "bug",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_solve.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_solve.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_solve.png-wvc-sizes",
  alt: "Mentor assisting child in debugging robotics circuit and error logs"
}, {
  step: "05",
  enTitle: "Create",
  viTitle: "Sáng Tạo",
  enDesc: "Custom project enhancements, creative mechanical styling, and group demonstrations.",
  viDesc: "Tự do sáng tạo tính năng mở rộng, hoàn thiện dự án và thuyết trình cùng đồng đội.",
  iconName: "sparkles",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_create.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_create.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_session_create.png-wvc-sizes",
  alt: "Confident student holding up a completed custom automated robotics invention"
}];
export default function SessionFlow() {
  const [lang, setLang] = useState<"en" | "vi">("en");
  const prefersReducedMotion = useReducedMotion();
  const renderIcon = (name: string) => {
    switch (name) {
      case "wrench":
        return <Wrench className="size-4 text-secondary-foreground" />;
      case "code":
        return <Code2 className="size-4 text-secondary-foreground" />;
      case "test":
        return <Activity className="size-4 text-secondary-foreground" />;
      case "bug":
        return <Bug className="size-4 text-secondary-foreground" />;
      case "sparkles":
        return <Sparkles className="size-4 text-secondary-foreground" />;
      default:
        return <Cpu className="size-4 text-secondary-foreground" />;
    }
  };
  return <section id="sessionflow" data-nav="light" className="relative w-full bg-background text-foreground py-20 lg:py-28 overflow-hidden isolate">
      {/* Blueprint Grid & Circuit Motif Background */}
      <div className="absolute inset-0 pointer-events-none opacity-40" aria-hidden="true">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--color-border)/0.5)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--color-border)/0.5)_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Precision 1px Lime-Green Spec Rail Accent */}
        <div className="relative z-10 w-full flex items-center justify-between mb-8 pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-secondary inline-block animate-pulse" />
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              SPEC // LAB PROTOCOL FLOW
            </span>
          </div>

          {/* Bilingual Switcher */}
          <div className="flex items-center gap-1 rounded-md border border-border bg-card p-0.5">
            <Button type="button" variant="ghost" size="sm" onClick={() => setLang("en")} className={`h-auto px-2.5 py-1 text-xs font-semibold rounded-sm transition-colors ${lang === "en" ? "bg-secondary text-secondary-foreground hover:bg-secondary hover:text-secondary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              EN
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setLang("vi")} className={`h-auto px-2.5 py-1 text-xs font-semibold rounded-sm transition-colors ${lang === "vi" ? "bg-secondary text-secondary-foreground hover:bg-secondary hover:text-secondary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              VI
            </Button>
          </div>
        </div>

        {/* Decorative Floating Mascot Visual */}
        <div className="hidden md:block absolute top-16 lg:top-20 right-4 sm:right-6 lg:right-8 z-0 pointer-events-none select-none" aria-hidden="true">
          <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt="" loading="lazy" decoding="async" className="w-24 h-24 md:w-28 md:h-28 lg:w-36 lg:h-36 bg-transparent object-contain pointer-events-none select-none drop-shadow-md" />
        </div>

        {/* Header Block */}
        <div className="relative z-10 max-w-xl lg:max-w-3xl mb-14 lg:mb-16">
          <Badge variant="outline" className="mb-3 rounded-md border-secondary/50 bg-secondary/15 text-secondary-foreground font-semibold uppercase tracking-wide text-xs px-2.5 py-1">
            {lang === "en" ? "Engineering Method" : "Quy Trình Học Kỹ Thuật"}
          </Badge>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            {lang === "en" ? "Inside a ROBOTO session." : "Trải nghiệm một buổi học tại ROBOTO."}
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            {lang === "en" ? "Students aged 4–16 discover real engineering discipline through five continuous beats, guided by mentors at our 1:6 class ratio." : "Học viên từ 4–16 tuổi rèn luyện tư duy kỹ thuật thực chiến qua năm nhịp học liền mạch, cùng sự đồng hành sát sao của giảng viên với tỉ lệ 1:6."}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-6 text-xs text-muted-foreground font-mono">
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-secondary" />
              <span>1:6 Mentor Guidance</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Cpu className="size-4 text-secondary" />
              <span>Ages 4–16 Academy</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
              <span>Circuit & Blueprint Method</span>
            </div>
          </div>
        </div>

        {/* 5-Stage Interconnected Pipeline */}
        <div className="relative">
          {/* Connecting Track Line for Desktop */}
          <div className="hidden lg:block absolute top-[110px] left-8 right-8 h-0.5 bg-border -z-0" aria-hidden="true">
            <div className="h-full bg-secondary/60 w-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 relative z-10">
            {beats.map((beat, index) => {
            const cardMotionProps = prefersReducedMotion ? {} : {
              initial: {
                opacity: 0,
                y: 16
              },
              whileInView: {
                opacity: 1,
                y: 0
              },
              viewport: {
                once: true,
                amount: 0.2
              },
              transition: {
                duration: 0.35,
                delay: index * 0.06,
                ease: [0.16, 1, 0.3, 1]
              }
            };
            return <motion.div key={beat.step} data-index={index} {...cardMotionProps} whileHover={{
              y: -4
            }} className="group flex flex-col h-full rounded-xl">
                  <Card className="flex flex-col h-full bg-card border border-border shadow-sm hover:border-secondary transition-[border-color,box-shadow] duration-200 overflow-hidden">
                    {/* Visual Lab Pane */}
                    <div className="relative w-full overflow-hidden bg-muted">
                      <AspectRatio ratio={16 / 10}>
                        <img src={beat.imgSrc} data-wvc-srcset={beat.imgSrcset} data-wvc-sizes={beat.imgSizes} alt={beat.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </AspectRatio>

                      {/* Step Indicator Pill & Circuit Squircle Icon */}
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded-md font-mono text-[11px] font-bold bg-background/90 text-foreground border border-border shadow-xs backdrop-blur-xs">
                          {beat.step}
                        </span>
                      </div>

                      <div className="absolute top-3 right-3 p-2 rounded-lg bg-secondary shadow-xs">
                        {renderIcon(beat.iconName)}
                      </div>
                    </div>

                    {/* Card Content Body */}
                    <CardContent className="flex flex-col flex-1 p-5 pt-4">
                      <div className="mb-2">
                        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground block mb-0.5">
                          {lang === "en" ? `Stage ${beat.step}` : `Giai đoạn ${beat.step}`}
                        </span>
                        <h3 className="font-serif text-xl font-bold text-foreground">
                          {lang === "en" ? beat.enTitle : beat.viTitle}
                        </h3>
                      </div>

                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed flex-1">
                        {lang === "en" ? beat.enDesc : beat.viDesc}
                      </p>

                      <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between text-[11px] font-mono text-muted-foreground">
                        <span className="text-muted-foreground/80">
                          {lang === "en" ? "Practice" : "Thực hành"}
                        </span>
                        <span className="text-secondary font-semibold flex items-center gap-0.5">
                          OK <span className="h-1 w-1 rounded-full bg-secondary" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Footer Callout & Action Bar */}
        <div className="mt-14 p-6 sm:p-8 rounded-xl border border-border bg-card shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h4 className="font-serif text-lg font-bold text-foreground">
              {lang === "en" ? "Ready to build your first robot?" : "Sẵn sàng sáng chế robot đầu tiên?"}
            </h4>
            <p className="mt-1 text-sm text-muted-foreground">
              {lang === "en" ? "Join a guided hands-on session at our learning centers." : "Tham gia buổi học thử nghiệm có hướng dẫn tại các trung tâm ROBOTO."}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-medium px-5">
              <Link to="/FreeTrialContactPage">
                {lang === "en" ? "Book a trial session" : "Đăng ký học thử"}
                <ArrowRight className="ml-1.5 size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-md border-border text-foreground hover:bg-accent hover:text-accent-foreground">
              <Link to="/LearningPathCurriculumPage">
                {lang === "en" ? "Explore curriculum" : "Xem lộ trình học"}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}